import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import classes from './classes.module.scss';
import { getDataFromStorage } from '../../../../../common/config/utils';
import { trackUrgencySignalShown } from '../../../../actions/advisors';

interface WindowWithCurrentUserId extends Window {
  currentUserId?: number;
}

type AdvisorUrgencyType = 'advisor_details' | 'advisor_header' | 'advisor_content' | 'advisor_cta';

const shouldShowUrgencySignal = (loading: boolean, isOnline: boolean, fbm: string) => {
  if (loading || !isOnline || fbm) return false;
  const wasLogin = getDataFromStorage('WAS_LOGIN');
  const isLoggedIn = !!(window as WindowWithCurrentUserId).currentUserId;
  const shouldShow = isLoggedIn || (!isLoggedIn && !wasLogin);
  return shouldShow;
};

export interface AdvisorUrgencyProps {
  advisorId: number;
  isOnline: boolean;
  type?: AdvisorUrgencyType;
  loading?: boolean;
  fbm?: string;
}

export interface AdvisorUrgencyMessage {
  icon: string;
  text: string;
}

const getStoredItems = (): Record<string, AdvisorUrgencyMessage> => {
  const stored = localStorage.getItem('advisor_urgency_signal');
  return stored ? JSON.parse(stored) : {};
};

const AdvisorUrgencySignal = ({
  advisorId, isOnline, type = 'advisor_details', loading = false, fbm
}: AdvisorUrgencyProps) => {
  const [urgencyMessage, setUrgencyMessage] = useState<AdvisorUrgencyMessage | null>(null);

  useEffect(() => {
    const storedItems = getStoredItems();
    setUrgencyMessage(storedItems?.[advisorId] || null);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'advisor_urgency_signal') {
        const newStoredItems = e.newValue ? JSON.parse(e.newValue) : {};
        setUrgencyMessage(newStoredItems[advisorId] || null);
      }
    };

    const handleCustomEvent = (e: CustomEvent) => {
      const { advisorId: updatedAdvisorId, urgencySignal } = e.detail;
      if (updatedAdvisorId === advisorId) {
        setUrgencyMessage(urgencySignal?.show && urgencySignal?.message ? urgencySignal.message : null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('advisorUrgencySignalUpdated', handleCustomEvent as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('advisorUrgencySignalUpdated', handleCustomEvent as EventListener);
    };
  }, [advisorId]);

  const containerClass = `${ classes.urgencyContainer } ${ classes[type] }`;

  const renderUrgencySignal = () => {
    const shouldShow = shouldShowUrgencySignal(loading, isOnline, fbm);
    if (!shouldShow) return null;

    trackUrgencySignalShown(urgencyMessage);
    window.dispatchEvent(new CustomEvent('urgencySignalRendered', {
      detail: { advisorId }
    }));

    if (!urgencyMessage) return null;

    return (
      <div className={ containerClass } data-urgency-signal="true" data-advisor-id={ advisorId } data-type={ type } data-testid={ type === 'advisor_content' ? 'urgency-signal-content' : 'urgency-signal' }>
        <div className={ classes.urgencyMessage }>
          {urgencyMessage.icon && <div className={ classes.emojiContainer }>{urgencyMessage.icon}</div>}
          <div className={ classes.urgencyMessageText }>
            <Trans
              components={ {
                b: <span className={ classes.colorText } />
              } }
            >
              {urgencyMessage.text}
            </Trans>
          </div>
        </div>
      </div>
    );
  };
  return renderUrgencySignal();
};

AdvisorUrgencySignal.propTypes = {
  advisorId: PropTypes.number.isRequired,
  isOnline: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  fbm: PropTypes.string
};

AdvisorUrgencySignal.defaultProps = {
  fbm: null
};

export default AdvisorUrgencySignal;
